/**
 * 从 SQL 文件解析教材名称的工具函数
 * 当数据库 SQL 文件更新时，前端可以自动同步
 */

export interface TextbookInfo {
  name: string
  tableName: string
  educationLevel?: string
  category?: string
}

/**
 * 从 SQL 注释中提取教材名称
 * 格式: COMMENT = '教材名称 教材内容表';
 */
export function parseTextbookName(comment: string): string {
  // 移除 "教材内容表" 后缀
  return comment.replace(/\s*教材内容表\s*$/, '').trim()
}

/**
 * 从表名提取教材名称（备用方法）
 * 格式: book_english_name -> 中文名称映射
 */
export function getTextbookNameFromTable(tableName: string): string {
  // 移除 book_ 前缀
  const name = tableName.replace(/^book_/, '')
  // 这里可以添加表名到中文名的映射
  // 或者通过API从数据库获取
  return name
}

/**
 * 教育层次映射
 */
export const EDUCATION_LEVEL_MAP: Record<string, string[]> = {
  undergraduate: [
    '大学英语', '大学体育', '发展心理学', '医古文', '马克思主义基本原理',
    '近代中国特色社会主义', '中国哲学与中医哲学', '科学哲学与科学逻辑',
    '中药化学', '中药分析', '中药炮制学', '中药药剂学', '中医基础理论',
    '中药学', '方剂学', '中医诊断学', '中医内科学(妇儿)', '中医外科学(骨伤五官)',
    '中医经典(内经伤寒金匮温病)', '组织胚胎解剖学', '生理病理药理学',
    '生物化学与分子生物学', '微生物与免疫学', '诊断学与内科学(妇儿)',
    '影像学与外科学', '大学数学(微率阵)', '大学物理(力热电光)',
    '程序设计基础', '控制理论', '计算机学', '人工智能导论', '数据库概论',
    '机器学习导论', '中国文化史', '中国医学史', '医学伦理学', '自然辨证法',
    '中药鉴定学与生药学', '药用植物学', '经络腧穴学', '针灸推拿学',
    '人机接口交互技术', '中医气功学', '复变函数', '经典场论', '智能硬件',
    '深度学习平台', '世界传统医学概论', '医学心理学', '系统科学(复杂性科学)',
    '智能健康感知', '中药药理学', '养生康复学', '中医各家学说',
    '中医训诂考据学', '仿生设计学', '生物进化', '量子力学', '量子场论',
    '多智能体系统', '机器人学导论',
  ],
  // 硕士阶段
  master: [
    // 文化
    '中国文化史', '中国医学史',
    // 哲学
    '医学伦理学', '自然辨证法',
    // 中药
    '中药鉴定学与生药学', '药用植物学',
    // 临床
    '经络腧穴学', '针灸推拿学',
    // 西医参考
    '人机接口交互技术', '中医气功学',
    // 智能
    '复变函数', '经典场论',
    // 设备
    '智能硬件', '深度学习平台',
  ],
  // 博士阶段
  doctoral: [
    // 文化
    '世界传统医学概论', '医学心理学',
    // 哲学
    '智能健康感知',
    // 中药
    '中药药理学', '养生康复学',
    // 临床
    '中医各家学说', '中医训诂考据学',
    // 西医参考
    '仿生设计学', '生物进化',
    // 智能
    '量子力学', '量子场论',
    // 设备
    '多智能体系统', '机器人学导论',
  ],
  postdoctoral: [
    '意志力', '敏感度', '太阳有太阴无?', '唯心唯物交集?', '顺势药效',
    '导引术', '能量医学', '五运六气概论', '风水疗效', '宇宙医学',
    '音乐治疗', '体育治疗', '仿生发声', '仿生运动',
  ],
  professionalInsight: [
    '灵感认知', '奇门遁甲', '宗教共识', '信仰混沌', '人民公社?',
    '梦景伤人?', '神灵疗法', '气功疗法', '佛教轮回', '道教辟谷',
    '复杂体系', '群智交互', '土星移居部落', '医患熵场感应',
  ],
}

/**
 * 学科分类映射
 */
export const CATEGORY_MAP: Record<string, string[]> = {
  culture: [
    '中国文化史', '中国医学史', '医古文', '论语', '大学', '易经', '道德经',
    '世界传统医学概论', '民族医学', '替代医学',
  ],
  philosophy: [
    '中国哲学与中医哲学', '科学哲学与科学逻辑', '马克思主义基本原理',
    '近代中国特色社会主义', '自然辨证法', '五运六气概论', '唯心唯物交集?',
    '太阳有太阴无?', '太阳太阴', '唯心唯物论',
  ],
  tcmClinical: [
    '中医基础理论', '方剂学', '中医诊断学', '中医内科学(妇儿)', '中医外科学(骨伤五官)',
    '中医经典(内经伤寒金匮温病)', '经络腧穴学', '针灸推拿学', '中医气功学',
    '中医各家学说', '中医训诂考据学', '养生康复学', '导引术', '气功疗法',
  ],
  herbal: [
    '中药化学', '中药分析', '中药炮制学', '中药药剂学', '中药学',
    '中药鉴定学与生药学', '药用植物学', '中药药理学', '生物酶药用', '顺势药效',
    '中药药性学', '中药量子学',
  ],
  westernClinical: [
    '组织胚胎解剖学', '生理病理药理学', '生物化学与分子生物学', '微生物与免疫学',
    '诊断学与内科学(妇儿)', '影像学与外科学', '医学心理学', '医学伦理学',
    '发展心理学', '解梦心理学', '精神依赖特质', '进化心理',
  ],
  intelligent: [
    '程序设计基础', '控制理论', '计算机学', '人工智能导论', '数据库概论',
    '机器学习导论', '人机接口交互技术', '智能硬件', '深度学习平台',
    '智能健康感知', '多智能体系统', '机器人学导论', '系统科学(复杂性科学)',
    '群智交互',
  ],
  equipment: [
    '大学数学(微率阵)', '大学物理(力热电光)', '复变函数', '经典场论',
    '量子力学', '量子场论', '仿生设计学', '生物进化', '化学进化',
    '天体物理学', '天网万物溯源',
  ],
  other: [
    '大学英语', '大学体育', '能量医学', '宇宙医学', '音乐治疗', '体育治疗',
    '仿生发声', '仿生运动', '灵感认知', '奇门遁甲', '宗教共识', '信仰混沌',
    '人民公社?', '梦景伤人?', '神灵疗法', '佛教轮回', '道教辟谷',
    '复杂体系', '土星移居部落', '医患熵场感应', '意志力', '敏感度',
    '风水疗效', '食物链炫',
  ],
}

/**
 * 根据教材名称获取教育层次
 */
export function getEducationLevel(textbookName: string): string | undefined {
  for (const [level, textbooks] of Object.entries(EDUCATION_LEVEL_MAP)) {
    if (textbooks.includes(textbookName)) {
      return level
    }
  }
  return undefined
}

/**
 * 根据教材名称获取学科分类
 */
export function getCategory(textbookName: string): string | undefined {
  for (const [category, textbooks] of Object.entries(CATEGORY_MAP)) {
    if (textbooks.includes(textbookName)) {
      return category
    }
  }
  return undefined
}

/**
 * 从 API 获取教材列表（经 apiClient，统一 Token 与错误处理）
 */
export async function fetchTextbooksFromAPI(): Promise<TextbookInfo[]> {
  try {
    const { getTextbookList } = await import('../api/textbooks')
    const data = await getTextbookList()
    return data.textbooks
  } catch (error) {
    console.error('Failed to fetch textbooks from API:', error)
    return []
  }
}

/**
 * 获取所有教材名称（从本地配置）
 * 当API不可用时使用此方法
 */
export function getAllTextbooks(): string[] {
  const allTextbooks: string[] = []
  Object.values(EDUCATION_LEVEL_MAP).forEach(textbooks => {
    allTextbooks.push(...textbooks)
  })
  // 去重
  return Array.from(new Set(allTextbooks))
}

